import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import {
  LayoutDashboard,
  Building2,
  Home,
  CreditCard,
  Wrench,
  Shield,
  Banknote,
  FileText,
  ClipboardList,
  Inbox,
  MessageSquare,
  BarChart3,
  UserCheck,
  Search,
  Calendar,
  Bell,
  Scale,
  TrendingUp,
  Settings,
  Camera,
  Wallet,
  Bot,
  Brain,
  Bookmark,
  Users,
  LogOut,
  AlertTriangle,
  Package,
  FilePenLine
} from 'lucide-react';

// Sidebar link component - mobile optimized
const SidebarLink = ({ to, icon: Icon, label, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={cn(
      "flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-lg transition-colors",
      "text-sm font-medium min-h-[44px]", // Touch-friendly min height
      active
        ? "bg-primary/10 text-primary"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    )}
  >
    <Icon className="w-5 h-5 flex-shrink-0" />
    <span className="truncate">{label}</span>
  </Link>
);

// Section header
const SidebarSection = ({ title, children }) => (
  <div className="mb-4">
    {title && (
      <h3 className="px-3 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
        {title}
      </h3>
    )}
    <div className="space-y-1">
      {children}
    </div>
  </div>
);

export const TenantSidebar = ({ onNavigate }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleClick = () => {
    if (onNavigate) onNavigate();
  };

  return (
    <nav className="w-64 bg-white border-r min-h-screen p-4">
      <SidebarSection>
        <SidebarLink to="/tenant/dashboard" icon={LayoutDashboard} label="Dashboard" active={isActive('/tenant/dashboard')} onClick={handleClick} />
        <SidebarLink to="/properties" icon={Search} label="Find Property" active={isActive('/properties')} onClick={handleClick} />
        <SidebarLink to="/tenant/saved-searches" icon={Bookmark} label="Saved Searches" active={isActive('/tenant/saved-searches')} onClick={handleClick} />
        <SidebarLink to="/tenant/applications" icon={ClipboardList} label="Applications" active={isActive('/tenant/applications')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="My Tenancy">
        <SidebarLink to="/tenant/tenancies" icon={Home} label="My Home" active={isActive('/tenant/tenancies')} onClick={handleClick} />
        <SidebarLink to="/tenant/payments" icon={CreditCard} label="Payments" active={isActive('/tenant/payments')} onClick={handleClick} />
        <SidebarLink to="/tenant/direct-debit" icon={Banknote} label="Direct Debit" active={isActive('/tenant/direct-debit')} onClick={handleClick} />
        <SidebarLink to="/tenant/maintenance" icon={Wrench} label="Repairs" active={isActive('/tenant/maintenance')} onClick={handleClick} />
        <SidebarLink to="/tenant/inspections" icon={Calendar} label="Inspections" active={isActive('/tenant/inspections')} onClick={handleClick} />
        <SidebarLink to="/tenant/documents" icon={FileText} label="Documents" active={isActive('/tenant/documents')} onClick={handleClick} />
        <SidebarLink to="/tenant/joint-tenancy" icon={Users} label="Joint Tenancy" active={isActive('/tenant/joint-tenancy')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="Account">
        <SidebarLink to="/tenant/credit-reporting" icon={TrendingUp} label="Credit Reporting" active={isActive('/tenant/credit-reporting')} onClick={handleClick} />
        <SidebarLink to="/tenant/my-cases" icon={MessageSquare} label="Messages" active={isActive('/tenant/my-cases')} onClick={handleClick} />
        <SidebarLink to="/tenant/notifications" icon={Bell} label="Notifications" active={isActive('/tenant/notifications')} onClick={handleClick} />
      </SidebarSection>
    </nav>
  );
};

export const LandlordSidebar = ({ onNavigate }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleClick = () => {
    if (onNavigate) onNavigate();
  };

  return (
    <nav className="w-64 bg-white border-r min-h-screen p-4">
      <SidebarSection>
        <SidebarLink to="/landlord/dashboard" icon={LayoutDashboard} label="Dashboard" active={isActive('/landlord/dashboard')} onClick={handleClick} />
        <SidebarLink to="/landlord/my-cases" icon={Inbox} label="My Cases" active={isActive('/landlord/my-cases')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="Properties">
        <SidebarLink to="/landlord/properties" icon={Building2} label="Properties" active={isActive('/landlord/properties')} onClick={handleClick} />
        <SidebarLink to="/landlord/applications" icon={ClipboardList} label="Applications" active={isActive('/landlord/applications')} onClick={handleClick} />
        <SidebarLink to="/landlord/tenancies" icon={Home} label="Tenancies" active={isActive('/landlord/tenancies')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="Operations">
        <SidebarLink to="/landlord/maintenance" icon={Wrench} label="Maintenance" active={isActive('/landlord/maintenance')} onClick={handleClick} />
        <SidebarLink to="/landlord/compliance" icon={Shield} label="Compliance" active={isActive('/landlord/compliance')} onClick={handleClick} />
        <SidebarLink to="/landlord/payments" icon={CreditCard} label="Payments" active={isActive('/landlord/payments')} onClick={handleClick} />
        <SidebarLink to="/landlord/rent-eviction" icon={AlertTriangle} label="Rent & Notices" active={isActive('/landlord/rent-eviction')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="Financial">
        <SidebarLink to="/landlord/financials" icon={Wallet} label="Financial Dashboard" active={isActive('/landlord/financials')} onClick={handleClick} />
        <SidebarLink to="/landlord/deposits" icon={Banknote} label="Deposits" active={isActive('/landlord/deposits')} onClick={handleClick} />
        <SidebarLink to="/finance/deposits" icon={Scale} label="Deposit Lifecycle" active={isActive('/finance/deposits')} onClick={handleClick} />
        <SidebarLink to="/landlord/statements" icon={BarChart3} label="Statements" active={isActive('/landlord/statements')} onClick={handleClick} />
        <SidebarLink to="/landlord/banking" icon={Building2} label="Banking" active={isActive('/landlord/banking')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="Support">
        <SidebarLink to="/cases" icon={MessageSquare} label="My Cases" active={isActive('/cases')} onClick={handleClick} />
      </SidebarSection>

      <SidebarSection title="Account">
        <SidebarLink to="/landlord/verification" icon={UserCheck} label="Verification" active={isActive('/landlord/verification')} onClick={handleClick} />
        <SidebarLink to="/landlord/documents" icon={FileText} label="Documents" active={isActive('/landlord/documents')} onClick={handleClick} />
        <SidebarLink to="/landlord/notifications" icon={Bell} label="Notifications" active={isActive('/landlord/notifications')} onClick={handleClick} />
      </SidebarSection>
    </nav>
  );
};

export const AdminSidebar = ({ onNavigate }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleClick = () => {
    if (onNavigate) onNavigate();
  };

  return (
    <nav className="w-64 bg-white border-r min-h-screen p-4">
      {/* Main Dashboard */}
      <SidebarSection>
        <SidebarLink to="/admin/console" icon={LayoutDashboard} label="Dashboard" active={isActive('/admin/console')} onClick={handleClick} />
      </SidebarSection>

      {/* Operations - Day-to-day management */}
      <SidebarSection title="Operations">
        <SidebarLink to="/admin/cases" icon={Inbox} label="Cases" active={isActive('/admin/cases')} onClick={handleClick} />
        <SidebarLink to="/admin/arrears" icon={Banknote} label="Arrears" active={isActive('/admin/arrears')} onClick={handleClick} />
        <SidebarLink to="/admin/vaas-calendar" icon={Calendar} label="VaaS Calendar" active={isActive('/admin/vaas-calendar')} onClick={handleClick} />
      </SidebarSection>

      {/* Users & Properties */}
      <SidebarSection title="Management">
        <SidebarLink to="/admin/users" icon={UserCheck} label="Users" active={isActive('/admin/users')} onClick={handleClick} />
        <SidebarLink to="/admin/properties" icon={Building2} label="Properties" active={isActive('/admin/properties')} onClick={handleClick} />
        <SidebarLink to="/admin/tenancies" icon={Home} label="Tenancies" active={isActive('/admin/tenancies')} onClick={handleClick} />
      </SidebarSection>

      {/* Finance */}
      <SidebarSection title="Finance">
        <SidebarLink to="/admin/finance/runs" icon={ClipboardList} label="Payment Runs" active={isActive('/admin/finance/runs')} onClick={handleClick} />
        <SidebarLink to="/admin/scottish-deposits" icon={FileText} label="Scottish Deposits" active={isActive('/admin/scottish-deposits')} onClick={handleClick} />
        <SidebarLink to="/admin/griffin" icon={Banknote} label="Griffin Banking" active={isActive('/admin/griffin')} onClick={handleClick} />
        <SidebarLink to="/admin/fiscal-automation" icon={Calendar} label="Fiscal Automation" active={isActive('/admin/fiscal-automation')} onClick={handleClick} />
      </SidebarSection>

      {/* Settings */}
      <SidebarSection title="Settings">
        <SidebarLink to="/admin/config" icon={Settings} label="Platform Config" active={isActive('/admin/config')} onClick={handleClick} />
        <SidebarLink to="/admin/docusign-templates" icon={FilePenLine} label="E-Signature Templates" active={isActive('/admin/docusign-templates')} onClick={handleClick} />
        <SidebarLink to="/admin/security" icon={Shield} label="Security" active={isActive('/admin/security')} onClick={handleClick} />
      </SidebarSection>

      {/* AI Agents */}
      <SidebarSection title="AI Agents">
        <SidebarLink to="/admin/ai-agents" icon={Bot} label="Agent Dashboard" active={isActive('/admin/ai-agents')} onClick={handleClick} />
        <SidebarLink to="/admin/agent-learning" icon={Brain} label="Agent Learning" active={isActive('/admin/agent-learning')} onClick={handleClick} />
      </SidebarSection>

      {/* Tools */}
      <SidebarSection title="Tools">
        <SidebarLink to="/admin/impersonate" icon={UserCheck} label="Impersonate User" active={isActive('/admin/impersonate')} onClick={handleClick} />
      </SidebarSection>
    </nav>
  );
};
